import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
	faBellConcierge,
	faBriefcase,
	faChevronDown,
	faDatabase,
	faFile,
	faMoneyBill,
	faRotateLeft,
	faStar,
	faTableCells,
	faUpload,
	faWaveSquare,
} from "@fortawesome/pro-regular-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { CatalogCategory } from "../config/tileCatalog";
import {
	ALL_CATEGORIES,
	CatalogCategorySelection,
	CatalogCounts,
	FAVORITES_CATEGORY,
} from "../utils/catalogFilters";
import { CATALOG_CATEGORY_ORDER } from "../utils/catalogGrouping";

interface CatalogRailProps {
	activeCategory: CatalogCategorySelection;
	counts: CatalogCounts;
	onSelectCategory: (category: CatalogCategorySelection) => void;
	/**
	 * Sticky top offset (px) so the rail (the sidebar on `md`+, or the collapsed category dropdown
	 * below `md`) pins directly beneath the sticky page header + AI search bar instead of sliding
	 * under them. Defaults to 20px when the caller hasn't measured the search-bar height yet.
	 */
	stickyTopPx?: number;
}

interface RailItem {
	key: CatalogCategorySelection;
	labelKey: string;
	icon: IconDefinition;
}

const categoryIcons: Record<CatalogCategory, IconDefinition> = {
	[CatalogCategory.UPLOAD]: faUpload,
	[CatalogCategory.SCRAPING]: faBellConcierge,
	[CatalogCategory.LINKEDIN]: faLinkedinIn as IconDefinition,
	[CatalogCategory.DATABASE]: faDatabase,
	[CatalogCategory.JOBS]: faBriefcase,
	[CatalogCategory.FUNDING]: faMoneyBill,
	[CatalogCategory.SIGNALS]: faWaveSquare,
	[CatalogCategory.REENGAGE]: faRotateLeft,
	[CatalogCategory.EMPTY]: faFile,
};

const categoryItems: RailItem[] = CATALOG_CATEGORY_ORDER.map((key) => ({
	key,
	labelKey: `categories.${key}`,
	icon: categoryIcons[key],
}));

export const CatalogRail: FunctionComponent<CatalogRailProps> = ({
	activeCategory,
	counts,
	onSelectCategory,
	stickyTopPx = 20,
}) => {
	const { t } = useTranslation();
	const tr = (key: string) => t(`list.create.catalog.rail.${key}`);

	// Below `md` the rail collapses to a single trigger that opens the full list (which would
	// otherwise stack full-width and push the catalog far down the page). At `md`+ it is always the
	// expanded sidebar, so this state is ignored there.
	const [isExpanded, setIsExpanded] = useState(false);

	const getLabelKey = (selection: CatalogCategorySelection): string => {
		if (selection === ALL_CATEGORIES) {
			return "all";
		}

		if (selection === FAVORITES_CATEGORY) {
			return "favorites";
		}

		return `categories.${selection}`;
	};

	const getIcon = (selection: CatalogCategorySelection): IconDefinition => {
		if (selection === ALL_CATEGORIES) {
			return faTableCells;
		}

		if (selection === FAVORITES_CATEGORY) {
			return faStar;
		}

		return categoryIcons[selection];
	};

	// Picking a category on the collapsed (mobile) rail also closes it, mirroring a select.
	const handleSelect = (selection: CatalogCategorySelection) => {
		onSelectCategory(selection);
		setIsExpanded(false);
	};

	const renderItem = ({ key, labelKey, icon }: RailItem, isStar = false) => {
		const isActive = activeCategory === key;

		return (
			<button
				key={key}
				className={classNames(
					"flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition",
					isActive
						? "bg-blue-action-200 font-semibold text-blue-action"
						: "text-text-meta hover:bg-background-light"
				)}
				type="button"
				onClick={() => handleSelect(key)}
			>
				<span
					className={classNames(
						"flex h-[18px] w-[18px] shrink-0 items-center justify-center",
						isStar ? "text-status-warning" : ""
					)}
				>
					<Icon className="h-4 w-4" icon={icon} />
				</span>
				<span className="flex-1 truncate">{tr(labelKey)}</span>
				<span
					className={classNames(
						"text-xs tabular-nums",
						isActive ? "text-blue-action" : "text-text-disabled"
					)}
				>
					{counts[key] ?? 0}
				</span>
			</button>
		);
	};

	return (
		<aside className="sticky z-10 w-full shrink-0 md:w-[188px]" style={{ top: `${stickyTopPx}px` }}>
			<button
				aria-expanded={isExpanded}
				className="mb-2 flex w-full cursor-pointer items-center gap-2.5 rounded-lg border border-border-light bg-white px-2.5 py-2 text-sm font-medium text-text transition duration-200 hover:border-brand-light md:hidden"
				type="button"
				onClick={() => setIsExpanded((value) => !value)}
			>
				<Icon className="h-4 w-4 text-text-meta" icon={getIcon(activeCategory)} />
				<span className="flex-1 truncate text-left">{tr(getLabelKey(activeCategory))}</span>
				<Icon
					className={classNames(
						"h-3.5 w-3.5 text-text-meta transition duration-200",
						isExpanded ? "rotate-180" : ""
					)}
					icon={faChevronDown}
				/>
			</button>

			<div className={classNames("flex-col gap-px md:flex", isExpanded ? "flex" : "hidden")}>
				{renderItem({ key: ALL_CATEGORIES, labelKey: "all", icon: faTableCells })}
				{renderItem({ key: FAVORITES_CATEGORY, labelKey: "favorites", icon: faStar }, true)}

				<div className="my-2 h-px bg-border-light" />

				{categoryItems.map((item) => renderItem(item))}
			</div>
		</aside>
	);
};
